<!-- When a video ends (Bespoke.vue), 2026-10-14: three VIZBI videos to watch next, over the player
     and its own size (recommendations.js picks them), and Replay. Vimeo's own end screen is set to
     Empty in its dashboard (Settings → Videos → Embed presets), so nothing of Vimeo's shows
     under it. Cards are the gallery's: thumbnail, title, and its lock icon for a video this viewer
     can't play yet (its page then offers Sign in or Register). No autoplay. Focus moves to the
     overlay as it appears, for keyboards and screen readers. -->
<template>
  <section ref="rootRef" class="endScreen" tabindex="-1" aria-labelledby="endScreenHeading">
    <h5 id="endScreenHeading" class="heading">Watch next</h5>
    <ul class="cards">
      <li v-for="item in cards" :key="item.hash">
        <router-link :to="item.path" class="card">
          <span class="thumbWrap">
            <img :src="item.thumbnail" alt="" class="thumb" />
            <span v-if="item.isFreeForSignIn" class="freeTag">Free with sign-up</span>
          </span>
          <span class="cardTitle">
            <span
              v-if="item.hasLock"
              class="lock"
              :class="{isUnlocked: item.isOpen}"
              role="img"
              :aria-label="item.isOpen ? 'Unlocked' : 'Locked'" />
            {{ item.name }}
          </span>
        </router-link>
      </li>
    </ul>
    <button type="button" class="replay" @click="$emit('replay')">↻ Replay</button>
  </section>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import useUserSafe from "@/composables/useUserSafe";
import {getHasVideoAccess, getSmallThumbnail, getVideoStatus} from "@/components/Video/videoCards";
import {getVideoPath} from "@/components/Video/videoPaths";

// options: the videos page's (showcases, get_privacy); items: [{hash, showcase}]
const props = defineProps({
  options: {type: Object, required: true},
  items: {type: Array, default: () => []}
});
defineEmits(["replay"]);

function getLock(video, item, viewer) {
  if (!props.options.get_privacy(item.hash, item.showcase)) return {};
  const access = {video, hash: item.hash, showcase: item.showcase, ...viewer};
  return {hasLock: true, isOpen: getHasVideoAccess(access)};
}

const {isSignedIn, user} = useUserSafe();
const rootRef = ref(null);
const cards = computed(() => {
  const viewer = {user: user.value, isSignedIn: isSignedIn.value};
  return props.items.map((item) => {
    const video = props.options.showcases[item.showcase].videos[item.hash];
    const thumbnail = getSmallThumbnail(props.options.showcases, item.showcase, item.hash);
    const needsSignIn = getVideoStatus(video) === "free"; // Free: sign-in only
    const isFreeForSignIn = needsSignIn && !viewer.isSignedIn;
    const path = `/Videos/${getVideoPath(props.options.showcases, item.hash) || item.hash}`;
    return {...item, name: video.name, path, thumbnail, isFreeForSignIn,
      ...getLock(video, item, viewer)};
  });
});

onMounted(() => rootRef.value?.focus({preventScroll: true}));
</script>

<style scoped>
/* over the player, its size: the wrapper's padding sets the height (Bespoke.vue #wrapper) */
.endScreen {
  position: absolute;
  inset: 0;
  z-index: 5;
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  box-sizing: border-box;
  color: #fff;
  background: #0a0a0c; /* opaque: nothing of the player shows through */
  text-align: left;
}
.endScreen:focus {
  outline: none;
}
.heading {
  margin: 0;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;
}
/* no bullets, whatever the global list styles */
.cards li {
  list-style: none;
  margin: 0;
  padding: 0;
}
.cards li::before,
.cards li::marker {
  content: none;
}
.card {
  display: block;
  color: #fff;
  text-decoration: none;
}
.card:hover .cardTitle,
.card:focus-visible .cardTitle {
  text-decoration: underline;
}
/* the thumbnail, its "free" tag placed on it (a free video, signed out: as the gallery's tag) */
.thumbWrap {
  position: relative;
  display: block;
}
.freeTag {
  position: absolute;
  left: 0.35rem;
  bottom: 0.35rem;
  padding: 0.1rem 0.45rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--orange-on-dark); /* the access buttons' orange (VideoAccessActionButton.vue) */
  background: rgba(0, 0, 0, 0.72);
  border-radius: 999px;
}
.card:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 3px;
  border-radius: 4px;
}
.thumb {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 4px;
}
.cardTitle {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-top: 0.35rem;
  font-size: 0.85rem;
  line-height: 1.25;
}
/* the padlock: about the title's capital height; locked in orange (needs buying), unlocked in the
   tags' grey (Tag.vue #a7a7a7: quiet metadata). The unlocked PNG is a shape filled with that grey
   (a mask), so its colour isn't baked into the image */
.lock {
  display: inline-block;
  height: 0.7em;
  vertical-align: baseline;
  background: url("~@/assets/img/lock-orange.png") center / contain no-repeat;
  aspect-ratio: 25 / 32;
}
.lock.isUnlocked {
  aspect-ratio: 25 / 35;
  background: #a7a7a7;
  mask: url("~@/assets/img/unlocked.png") center / contain no-repeat;
  -webkit-mask: url("~@/assets/img/unlocked.png") center / contain no-repeat;
}
/* the global button look reset: a quiet text button */
.replay {
  align-self: flex-start;
  margin: 0;
  padding: 0.3rem 0.8rem;
  font: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  color: #fff;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  box-shadow: none;
  cursor: pointer;
}
.replay:hover,
.replay:focus-visible {
  background: rgba(255, 255, 255, 0.26);
}
.replay:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}
/* a small player (phones): two cards, smaller text */
@container (max-width: 480px) {
  .endScreen {
    gap: 0.5rem;
    padding: 0.6rem 0.75rem;
  }
  .cards {
    grid-template-columns: repeat(2, 1fr);
  }
  .cards li:nth-child(3) {
    display: none;
  }
  .heading,
  .cardTitle,
  .replay {
    font-size: 0.75rem;
  }
}
</style>
